// Production additions for the rotary era (M2_SPEC §15). Called from production.js at clean hook points;
// everything here is gated on yf >= 1901 (or on M2-only data keys) so Pioneer production is unchanged.
//   water invasion (Golden Lane): water cut ramps 0 → 100% over rampDays once remaining/initial < atRemaining (+0.1 × dist)
//   water drive (East Texas, Yates): pressure falls at half the normal rate; water cut after 60% depletion
//   gas cap: wide-open flush production without reservoir engineering wastes the cap (RF −0.03, once per reservoir)
//   air/gas lift: only while pressure ratio > minPressureRatio, at rateMult
//   annular gas/water behind uncemented casing: production × 0.8 until squeezed (cement.js)
//   rod parts: pumped wells lose 1%/month to random downtime (pumper on staff: −50%)
//   condensate: price × 1.2; flare-gas fields: fire/H₂S exposure and, from flaringLimits, a daily fine
//   evaporation in storage: evapPerDay (earthen pits, cone roofs; floating roofs ~0)
//   opex/well/day: 1901 $0.78 → 1930 $3 → 1945 $4 (DESIGN §5.5)
import { chance, int } from '../core/rng.js';
import { WASTE } from '../data/m2/rules.js';
import { eqDef, fieldDef, stat, eraDollars, yf } from './defs.js';
import { reservoirsAt } from './geo.js';
import { hasUpgrade } from './research.js';
import { pumperOnStaff } from './automation.js';
import { flagOn } from './m2util.js';
import { clamp, notify, spend, fmt$, lerp } from './util.js';

const OPEX_M2 = [[1901, 0.78], [1930, 3], [1945, 4], [1980, 150], [2020, 400]];
export function opexM2(year) {
  const k = OPEX_M2;
  if (year <= k[0][0]) return k[0][1];
  for (let i = 1; i < k.length; i++) if (year <= k[i][0]) return lerp(k[i - 1][1], k[i][1], (year - k[i - 1][0]) / (k[i][0] - k[i - 1][0]));
  return k[k.length - 1][1];
}

// Reservoir definition by id (static), cached per field.
const RES_CACHE = new Map();
export function resDef(fieldId, resId) {
  let m = RES_CACHE.get(fieldId);
  if (!m) {
    m = {};
    for (const p of fieldDef(fieldId)?.prospects ?? []) for (const r of p.reservoirs ?? []) m[r.id] = r;
    RES_CACHE.set(fieldId, m);
  }
  return m[resId] ?? null;
}

// Pressure ratio for a reservoir (production.js daily): water drive halves the decline.
export function reservoirPressure(fieldId, resId, res) {
  const ratio = res.initial > 0 ? res.remaining / res.initial : 0;
  const d = resDef(fieldId, resId);
  const base = d?.waterDrive ? 1 - 0.5 * (1 - ratio) : ratio;
  return clamp(base + (res.shutInRegain ?? 0), 0, 1);
}

// Water cut 0..1 for a producing well.
export function waterCut(state, well, res) {
  const d = resDef(well.fieldId, well.reservoirId);
  if (!d || !res || res.initial <= 0) return well.annular?.kind === 'water' ? 0.2 : 0;
  const ratio = res.remaining / res.initial;
  let cut = 0;
  if (d.waterInvasion) {
    const dist = state.fields[well.fieldId].plots[well.plot]?.truth?.dist ?? 0;
    const start = d.waterInvasion.atRemaining + 0.1 * dist;
    if (ratio < start) {
      well.waterSince ??= state.day;
      cut = clamp((state.day - well.waterSince) / Math.max(1, d.waterInvasion.rampDays ?? 90), 0, 1);
    }
  } else if (d.waterDrive && ratio < 0.4) cut = clamp((0.4 - ratio) / 0.4, 0, 0.95);
  if (well.annular?.kind === 'water') cut = Math.max(cut, 0.2);
  return cut;
}

// Multiplier on a producing well's natural rate (after the M1 lift rule). Returns { mult, lift } — lift false means
// the installed lift does not work at this pressure (treated as no lift).
export function rateMult(state, well, res, pressure, needsLift) {
  let mult = 1;
  let liftOk = !!well.lift;
  if (well.lift) {
    const ldef = eqDef(well.lift.defId);
    const minP = stat(ldef, 'minPressureRatio', 0);
    if (pressure <= minP) liftOk = false;
    else if (needsLift) mult *= stat(ldef, 'rateMult', 1);
  }
  if (yf(state) < 1901) return { mult, liftOk };
  if (well.annular) mult *= 0.8;
  const cut = waterCut(state, well, res);
  well.waterCut = Math.round(cut * 1000) / 1000;
  mult *= 1 - cut;
  if (cut >= 0.98 && !well._wetNotified) {
    well._wetNotified = true;
    notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'WATERED_OUT', level: 'warn', text: `${well.name} has gone to salt water. Plug it.` });
  }
  // rod parts on pumped wells
  if (well.lift && well.prod) {
    if ((well.prod.rodDownUntil ?? 0) > state.day) mult = 0;
    else if (chance(state, (0.01 / 30) * (pumperOnStaff(state) ? 0.5 : 1))) {
      well.prod.rodDownUntil = state.day + int(state, 3, 7);
      mult = 0;
    }
  }
  return { mult, liftOk };
}

// Once per reservoir: flush production with the gas cap blown off wastes recovery (RF −0.03); reservoir engineering
// holding back-pressure adds +0.05 instead.
export function gasCapOnFirstProduction(state, well, res) {
  const d = resDef(well.fieldId, well.reservoirId);
  if (!d?.gasCap || res.gasCapDone || yf(state) < 1901) return;
  res.gasCapDone = true;
  const rf = d.rf ?? 0.15;
  const delta = hasUpgrade(state, 'reservoir_engineering') ? 0.05 : -0.03;
  const add = Math.round(res.initial * delta / rf);
  res.initial = Math.max(1, res.initial + add);
  res.remaining = Math.max(0, res.remaining + add);
}

// Condensate share of a field's production today → price × (1 + 0.2 × share).
export function condensateMult(state, fieldId) {
  const f = state.fields[fieldId];
  return 1 + 0.2 * clamp(f?.condShare ?? 0, 0, 1);
}

export function noteCondensate(state, fieldId, byRes) {
  const f = state.fields[fieldId];
  let tot = 0, cond = 0;
  for (const [rid, q] of Object.entries(byRes)) { tot += q; if (resDef(fieldId, rid)?.condensate) cond += q; }
  f.condShare = tot > 0 ? Math.round((cond / tot) * 1000) / 1000 : 0;
}

// Flare-gas fields (Turner Valley): daily fine from flaringLimits while anything flows.
export function flareDay(state, fieldId, produced) {
  const fdef = fieldDef(fieldId);
  if (!fdef?.flareGas || !(produced > 0) || !flagOn(state, WASTE.flareFlag)) return;
  const fine = eraDollars(state, WASTE.flareFinePerDay, WASTE.baseYear);
  spend(state, fine, 'other', false);
  const f = state.fields[fieldId];
  if (!f.flareWarned) { f.flareWarned = true; notify(state, 'news', { fieldId, kind: 'regulation', level: 'warn', text: `The Conservation Board fines ${fdef.name} ${fmt$(fine)} a day for flaring gas. Shut in or pay.` }); }
}

// Evaporation from storage (light ends). Returns bbl lost today.
export function evaporation(state, f, tanks, tankOil) {
  if (tankOil <= 0 || f.storage.capacityBbl <= 0) return 0;
  let e = 0;
  for (const t of tanks) {
    const per = stat(eqDef(t.defId), 'evapPerDay', 0);
    if (per > 0) e += tankOil * (stat(eqDef(t.defId), 'capacityBbl', 0) / f.storage.capacityBbl) * per;
  }
  if (e > 0 && yf(state) >= WASTE.from) {
    const fine = e * eraDollars(state, WASTE.finePerBbl, WASTE.baseYear);
    if (fine > 0) { spend(state, fine, 'other', false); state.company.stats.wasteBbl = (state.company.stats.wasteBbl ?? 0) + e; state.company.stats.wasteFines = (state.company.stats.wasteFines ?? 0) + fine; }
  }
  return e;
}

export function revealReservoirPressure(state, well) {
  const r = reservoirsAt(fieldDef(well.fieldId), state.fields[well.fieldId].plots[well.plot]).find((x) => x.id === well.reservoirId);
  if (!r) return;
  const key = `r:${r.id}`;
  const f = state.fields[well.fieldId];
  const v = Math.round((r.pore ?? 9) * 10) / 10;
  (f.zonePore ??= {})[key] ??= v;
  (well.zonePore ??= {})[key] ??= v;
}
